import React from 'react';
import styled from 'styled-components';

const VisionContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  color: #fff;
  padding: 80px 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const MainTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  margin-bottom: 30px;
  opacity: 0.9;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const MainContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 60px 15px;
  }
`;

const IntroSection = styled.div`
  background: #f8f9fa;
  padding: 50px 40px;
  border-radius: 15px;
  margin-bottom: 60px;
  text-align: center;

  h2 {
    font-size: 28px;
    color: #2c5aa0;
    margin-bottom: 25px;
    font-weight: 600;
  }

  p {
    font-size: 18px;
    color: #555;
    line-height: 1.7;
    margin-bottom: 15px;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
    
    h2 {
      font-size: 24px;
    }
    
    p {
      font-size: 16px;
    }
  }
`;

const VisionSection = styled.div`
  margin-bottom: 50px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const VisionNumber = styled.div`
  display: inline-block;
  background: #2c5aa0;
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
`;

const VisionTitle = styled.h3`
  font-size: 26px;
  color: #2c5aa0;
  margin-bottom: 25px;
  font-weight: 600;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const VisionContent = styled.div`
  background: #fff;
  padding: 35px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  border-left: 5px solid #2c5aa0;

  p {
    font-size: 17px;
    color: #444;
    line-height: 1.8;
    margin-bottom: 20px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 25px 20px;
    
    p {
      font-size: 16px;
    }
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(120deg, #ffd700 0%, #ffed4e 100%);
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  color: #333;
`;

const Vision: React.FC = () => {
  return (
    <VisionContainer>
      <HeroSection>
        <HeroContent>
          <MainTitle>교회 비전</MainTitle>
          <Subtitle>
            하나님의 말씀을 중심으로 하나님의 뜻을 따라가는 교회
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <IntroSection>
          <h2>얼바인중앙침례교회</h2>
          <p>
            <strong>한국의 강남중앙침례교회의 지교회</strong>이며,
          </p>
          <p>
            <strong>성경 말씀을 중심으로 하나님의 뜻을 따라가는 교회</strong>입니다.
          </p>
        </IntroSection>

        <VisionSection>
          <VisionNumber>1</VisionNumber>
          <VisionTitle>
            "<HighlightText>진리의 말씀(The Word of the Truth)</HighlightText>"으로 부흥을 소망하는 교회입니다.
          </VisionTitle>
          <VisionContent>
            <p>
              얼바인중앙침례교회는 사도바울과 종교개혁자 존 칼빈과 오직 성경으로 부흥을 이끌었던 조지 휫필드, 
              찰스 스펄전 목사님과 같이 <strong>"하나님의 주권"</strong>을 강조하는 청교도 신학을 근거로 
              <HighlightText>&lt;성경을&gt;</HighlightText> 가르치고,
            </p>
            <p>
              생명의 말씀을 선포하여 말씀에 따라 역사하시는 하나님의 주권에 의한 
              <strong>참된 부흥을 소망하는 교회</strong>입니다.
            </p>
          </VisionContent>
        </VisionSection>

        <VisionSection>
          <VisionNumber>2</VisionNumber>
          <VisionTitle>
            <HighlightText>월드미니스트리(W.O.R.L.D. Ministry)</HighlightText> 사역을 하는 교회입니다.
          </VisionTitle>
          <VisionContent>
            <p>
              세계와 미국과 한국을 그리스도께로 올려드리기 위한 
              <strong>월드미니스트리(W.O.R.L.D. Ministry)사역</strong>을 통해
            </p>
            <p>
              <strong>예수 그리스도의 복음을 전하고 하나님의 나라를 확장해 나아가는 교회</strong>입니다.
            </p>
          </VisionContent>
        </VisionSection>

        <VisionSection>
          <VisionNumber>3</VisionNumber>
          <VisionTitle>
            차세대들(Next Generation)에게 "<HighlightText>성경적 세계관과 비전</HighlightText>"을 제시하는 교회입니다.
          </VisionTitle>
          <VisionContent>
            <p>
              우리의 자녀들은 지극히 인본주의적인 세상적 가치관 속에서 살아가고 있습니다. 
              무엇이 참이며 거짓인지조차도 구별하기 어려운 혼돈의 시대 속에 살아갑니다.
            </p>
            <p>
              얼바인중앙침례교회는 우리의 자녀들에게 <strong>하나님의 말씀을 올바르게 가르쳐</strong> 
              바른 성경적 세계관으로 진리의 길을 갈 수 있도록 안내하는 교회입니다.
            </p>
            <p>
              이를 통해 삶 속에서 실제적으로 일하시며 자녀들을 통해 <HighlightText>"영광"</HighlightText>을 
              나타내시는 하나님을 만날 수 있도록, 꿈을 잃어가는 세대에게 세상을 두려워하지 않고 
              <strong>하나님이 주시는 참된 비전을 가질 수 있도록</strong> 기도하며 돕는 교회입니다.
            </p>
          </VisionContent>
        </VisionSection>
      </MainContent>
    </VisionContainer>
  );
};

export default Vision;


